/**
 * What the admin offers on a phone (SAM-944, mocks M4 and M5, approved 15 Oct 2026).
 *  - full:    works on a phone as it is (lists as cards where the mocks say so).
 *  - view:    display-only screens; a banner says «View only on a phone».
 *  - desktop: editors and configuration; the phone gets «Use a desktop for this».
 * A route that is not listed is `full`, so nothing is blocked by omission. First match wins.
 */
export type PhoneAccess = 'full' | 'view' | 'desktop';

export interface PhoneRule {
  pattern: RegExp;
  /** Optional: the rule applies only when the query string matches too (a tab of a page). */
  search?: RegExp;
  access: PhoneAccess;
  /** Name used in «<label> needs a bigger screen». */
  label: string;
}

export const PHONE_RULES: PhoneRule[] = [
  // Phone-usable: the daily work (passes, users and requests, arrivals, the dashboard).
  { pattern: /^\/(dashboard)?$/, access: 'full', label: 'Dashboard' },
  { pattern: /^\/invitations(\/|$)/, access: 'full', label: 'Passes' },
  { pattern: /^\/users(\/|$)/, access: 'full', label: 'Users' },
  { pattern: /^\/arrivals$/, access: 'full', label: 'Arrivals' },
  // SAM-1074: Activity (the old Bookings page) is drawn at 375 px (canvas BH-Cm).
  { pattern: /^\/bookings$/, access: 'full', label: 'Activity' },
  { pattern: /^\/contacts$/, access: 'full', label: 'Contacts' },

  // Read-only screens: they show data and have no editing control of their own.
  { pattern: /^\/business\/list$/, access: 'view', label: 'Businesses' },
  { pattern: /^\/eventsad\/list$/, access: 'view', label: 'Events' },
  { pattern: /^\/eventsad\/[^/]+\/(preview|dashboard)$/, access: 'view', label: 'Events' },

  // SAM-1073: the New service flow is drawn at 375 px (canvas 21 oct); the full editor stays desktop-only.
  { pattern: /^\/services\/new$/, access: 'full', label: 'New service' },
  { pattern: /^\/eventsad\/new$/, access: 'full', label: 'New event' },

  // SAM-1075: the member's file (Team tab of a business) is drawn at 375 px (canvas TM4m); the other tabs stay desktop-only.
  { pattern: /^\/business\/(?!list$|new$)[^/]+$/, search: /(^|[?&])tab=team(&|$)/, access: 'full', label: 'Team' },

  // SAM-944: the business's Activity (the tab a business page opens on) is drawn at 375 px (canvas PhoneActivity*).
  { pattern: /^\/business\/(?!list$|new$)[^/]+$/, search: /^(?!.*[?&]tab=(?!activity(&|$)))/, access: 'full', label: 'Activity' },

  // Editors and configuration: desktop only.
  { pattern: /^\/business(\/|$)/, access: 'desktop', label: 'Business' },
  { pattern: /^\/(products|services)(\/|$)/, access: 'desktop', label: 'Services and Products' },
  { pattern: /^\/payments$/, access: 'desktop', label: 'Transactions' },
  { pattern: /^\/estates(\/|$)/, access: 'desktop', label: 'Estates' },
  { pattern: /^\/property-admin$/, access: 'desktop', label: 'Property Admin' },
  { pattern: /^\/eventsad(\/|$)/, access: 'desktop', label: 'Events' },
  { pattern: /^\/settings\/tenant$/, access: 'desktop', label: 'Tenant Settings' },
  { pattern: /^\/settings(\/|$)/, access: 'desktop', label: 'Settings' },
  { pattern: /^\/users-demo$/, access: 'desktop', label: 'Users' },
];

export interface PhoneAccessResult {
  access: PhoneAccess;
  label: string;
}

export const phoneAccessFor = (pathname: string, search = ''): PhoneAccessResult => {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const rule = PHONE_RULES.find((r) => r.pattern.test(path) && (!r.search || r.search.test(search)));
  return rule ? { access: rule.access, label: rule.label } : { access: 'full', label: '' };
};
